/* The ordering "brain": turns a guest utterance into a spoken reply + cart
   actions. Prefers the OpenAI-backed `ai-voice` edge function; if that isn't
   configured (no key) or fails, falls back to a built-in offline matcher so
   voice ordering always works. */

import { supabase } from '../supabase';
import type { MenuItem } from '../data';

export interface VoiceAction { op: 'add' | 'remove' | 'set'; itemId: string; qty: number; }
export interface BrainResult {
  reply: string;
  actions: VoiceAction[];
  /** Item ids of EVERY dish the reply refers to — the UI shows a card for each,
   *  so the guest can see what's being talked about, not just hear it. */
  suggest: string[];
  done?: boolean;
  /** Set by the on-device matcher when it understood the ENTIRE utterance, so
   *  the caller knows it can skip the model. Not meaningful on AI results. */
  confident?: boolean;
  /** Which path answered — useful for measuring how often you actually pay. */
  source?: 'local' | 'ai';
}

export interface CartLite { name: string; qty: number; }

/** One prior turn of the conversation, oldest first. */
export interface ChatTurn { role: 'user' | 'assistant'; content: string; }

const NUM_WORDS: Record<string, number> = {
  a: 1, an: 1, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6,
  seven: 7, eight: 8, nine: 9, ten: 10, couple: 2, 'a couple': 2, half: 1, dozen: 12,
  // Hinglish numerals — "do butter naan aur ek paneer tikka" is one of the most
  // common things an Indian guest says, and it should never cost a token.
  ek: 1, do: 2, teen: 3, char: 4, char4: 4, paanch: 5, panch: 5,
  chhe: 6, che: 6, saat: 7, aath: 8, nau: 9, das: 10, dus: 10,
};

/** Words that carry no item meaning: polite filler, ordering verbs, joiners —
 *  English and Hinglish. Used ONLY to decide whether the local matcher
 *  understood the whole sentence; see [confidentLocal]. */
const STOP_WORDS = new Set([
  // English verbs / filler
  'add', 'order', 'want', 'wants', 'get', 'give', 'bring', 'take', 'need',
  'like', 'have', 'put', 'please', 'plz', 'i', 'we', 'me', 'my', 'us', 'the',
  'a', 'an', 'and', 'also', 'more', 'of', 'for', 'some', 'ok', 'okay', 'yes',
  'yeah', 'yep', 'sure', 'thanks', 'thank', 'you', 'will', 'would', 'can',
  'could', 'let', 'us', 'to', 'with', 'plus', 'another', 'extra',
  // remove-intent verbs (the matcher reads these separately)
  'remove', 'cancel', 'delete', 'off', 'no', 'not', 'dont', 'nahi',
  // Hinglish filler / verbs
  'aur', 'chahiye', 'chaiye', 'dedo', 'de', 'do', 'dijiye', 'karo', 'kar',
  'bhi', 'ka', 'ke', 'ki', 'mujhe', 'hume', 'hamein', 'bas', 'ji', 'lao',
  'lelo', 'lena', 'dena', 'ek', 'sath', 'saath', 'wala', 'wali',
]);

function norm(s: string): string { return s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim(); }

function resolveByName(name: string, menu: MenuItem[]): MenuItem | null {
  const n = norm(name);
  if (!n) return null;
  let hit = menu.find((m) => norm(m.name) === n);
  if (hit) return hit;
  hit = menu.find((m) => norm(m.name).includes(n) || n.includes(norm(m.name)));
  if (hit) return hit;
  // word-overlap fallback
  const words = new Set(n.split(' '));
  let best: { m: MenuItem; score: number } | null = null;
  for (const m of menu) {
    const mw = norm(m.name).split(' ');
    const score = mw.filter((w) => words.has(w)).length / mw.length;
    if (score >= 0.5 && (!best || score > best.score)) best = { m, score };
  }
  return best?.m ?? null;
}

/* ---------------- OpenAI path ---------------- */
async function viaOpenAI(
  transcript: string,
  name: string,
  menu: MenuItem[],
  cart: CartLite[],
  restaurant: string,
  history: ChatTurn[],
): Promise<BrainResult | null> {
  try {
    const { data, error } = await supabase.functions.invoke('ai-voice', {
      body: {
        transcript, name, restaurant, cart, history,
        // Only send what the model needs to reason about — never images.
        menu: menu
          .filter((m) => m.available)
          .map((m) => ({ id: m.id, name: m.name, price: m.price, category: m.category, foodType: m.foodType, recommended: m.recommended })),
      },
    });
    if (error || !data || (data as { error?: string }).error) return null;
    const d = data as {
      reply?: string;
      actions?: { op: string; name: string; qty: number }[];
      mention?: string[];
      suggest?: string[];
      done?: boolean;
    };
    const actions: VoiceAction[] = [];
    for (const a of d.actions ?? []) {
      const item = resolveByName(a.name, menu);
      // Never act on a dish that's sold out — acknowledge, don't add.
      if (item && item.available) {
        actions.push({
          op: (a.op as VoiceAction['op']) || 'add',
          itemId: item.id,
          qty: Math.max(1, Math.round(a.qty || 1)),
        });
      }
    }
    // Show a card for every dish the reply talks about, plus anything it just
    // added — the guest should always SEE what they're hearing about.
    const shown = new Set<string>();
    for (const s of [...(d.mention ?? []), ...(d.suggest ?? [])]) {
      const id = resolveByName(s, menu)?.id;
      if (id) shown.add(id);
    }
    for (const a of actions) shown.add(a.itemId);
    return {
      reply: d.reply || 'Sure.',
      actions,
      suggest: [...shown],
      done: d.done === true,
    };
  } catch {
    return null;
  }
}

/** Did the local matcher understand the WHOLE sentence?
 *
 *  This is the gate that decides whether we can skip the AI (and its tokens).
 *  The rule: after removing the words we matched to dish names, plus numbers
 *  and filler, is anything left over?
 *
 *   "two butter naan"            → two=number, butter naan=dish  → nothing left → LOCAL
 *   "do paneer tikka aur ek naan"→ all accounted                 → nothing left → LOCAL
 *   "is the paneer tikka spicy?" → matches a dish, but "is/spicy" left → ASK AI
 *   "something not too spicy"    → no dish at all                → ASK AI
 *
 *  That leftover check is what stops the classic failure of a keyword matcher:
 *  answering a QUESTION about a dish by silently adding it to the cart. If we
 *  don't understand every word, we don't guess — we escalate. */
function confidentLocal(transcript: string, matchedNames: string[]): boolean {
  if (matchedNames.length === 0) return false;
  const words = norm(transcript).split(' ').filter(Boolean);
  // Words consumed by the dish names we matched.
  const consumed = new Set<string>();
  for (const n of matchedNames) for (const w of norm(n).split(' ')) consumed.add(w);
  for (const w of words) {
    if (consumed.has(w)) continue;
    if (STOP_WORDS.has(w)) continue;
    if (/^\d+$/.test(w) || NUM_WORDS[w] !== undefined) continue;
    return false; // a word we can't explain — let the model read it
  }
  return true;
}

/* ---------------- Offline fallback matcher ---------------- */
function viaLocal(transcript: string, menu: MenuItem[]): BrainResult {
  const t = norm(transcript);
  // Nothing was said — there's nothing for a model to interpret either.
  if (!t) {
    return {
      reply: "I didn't catch that — what would you like to order?",
      actions: [], suggest: [], confident: true,
    };
  }

  // checkout intent — unambiguous, so answer locally and spend nothing
  if (/\b(that is all|thats all|that's all|im done|i am done|done|checkout|place order|finish|nothing else|bas|bas itna|itna hi)\b/.test(t)) {
    return {
      reply: 'Great! Tap Place Order whenever you are ready, or tell me anything else to add.',
      actions: [], suggest: [], done: true, confident: true,
    };
  }

  // recommendations
  if (/\b(recommend|suggest|popular|best|special|what.* good|chef)\b/.test(t)) {
    const recs = (menu.filter((m) => m.recommended && m.available).slice(0, 3));
    const picks = recs.length ? recs : menu.filter((m) => m.available).slice(0, 3);
    const names = picks.map((m) => m.name).join(', ');
    return {
      reply: picks.length ? `I'd recommend our ${names}. Want me to add any of those?` : "Let me know what you're in the mood for!",
      actions: [], suggest: picks.map((m) => m.id),
    };
  }

  const removing = /\b(remove|cancel|delete|take off|no|nahi|hatao)\b/.test(t);
  const words = t.split(' ');
  const actions: VoiceAction[] = [];
  const added: string[] = [];
  const matchedNames: string[] = [];

  for (const m of menu) {
    if (!m.available) continue;
    const mn = norm(m.name);
    const mWords = mn.split(' ');
    // match if the full name appears, or all significant (3+ char) words appear
    const sig = mWords.filter((w) => w.length >= 3);
    const present = t.includes(mn) || (sig.length > 0 && sig.every((w) => words.includes(w)));
    if (!present) continue;
    matchedNames.push(m.name);

    // quantity: number immediately before the first matched word
    let qty = 1;
    const firstWord = mWords[0];
    const idx = words.indexOf(firstWord);
    if (idx > 0) {
      const prev = words[idx - 1];
      if (/^\d+$/.test(prev)) qty = parseInt(prev, 10);
      else if (NUM_WORDS[prev] !== undefined) qty = NUM_WORDS[prev];
    }
    actions.push({ op: removing ? 'remove' : 'add', itemId: m.id, qty });
    added.push(`${qty}× ${m.name}`);
  }

  if (actions.length === 0) {
    return { reply: "Hmm, I couldn't find that on the menu. You can say something like “two butter naan and a paneer butter masala”, or ask me to recommend something.", actions: [], suggest: [] };
  }
  const verb = removing ? 'Removed' : 'Added';
  return {
    reply: `${verb} ${added.join(' and ')}. Anything else?`,
    actions,
    // Show a card for what we just changed, exactly as the AI path does.
    suggest: actions.map((a) => a.itemId),
    confident: confidentLocal(transcript, matchedNames),
  };
}

/** Main entry: interpret one utterance.
 *
 *  [ctx.history] is the conversation so far (oldest first). Passing it is what
 *  lets a guest say "make it two" or "actually remove that" — without it every
 *  turn starts from nothing. The offline fallback has no memory, so it only
 *  handles self-contained utterances; that's the trade for working with no key
 *  and no internet. */
export async function interpret(
  transcript: string,
  ctx: {
    name: string;
    restaurant: string;
    menu: MenuItem[];
    cart: CartLite[];
    history?: ChatTurn[];
  },
): Promise<BrainResult> {
  // LOCAL FIRST. Most of what a guest says is plain — "two butter naan",
  // "paneer tikka", "do roti aur ek lassi", "that's all". The on-device matcher
  // handles those instantly, offline, and for zero tokens. We only pay for the
  // model when the sentence contains something it couldn't account for.
  //
  // Deliberately NOT a keyword shortcut: [confidentLocal] requires EVERY word
  // to be explained. "Is the paneer tikka spicy?" matches a dish but leaves
  // "is" and "spicy" unexplained, so it escalates instead of silently adding
  // biryani to someone's cart — which is how naive matchers embarrass you.
  const local = viaLocal(transcript, ctx.menu);
  if (local.confident) return { ...local, source: 'local' };

  const ai = await viaOpenAI(
    transcript, ctx.name, ctx.menu, ctx.cart, ctx.restaurant, ctx.history ?? []);
  if (ai && (ai.actions.length > 0 || ai.suggest.length > 0 || ai.reply)) {
    return { ...ai, source: 'ai' };
  }
  // No key, no internet, or the model failed — the local read is still better
  // than nothing, even when it wasn't sure.
  return { ...local, source: 'local' };
}

/** First-turn greeting (spoken before the guest says anything). */
export function greeting(name: string, restaurant: string): string {
  const who = name ? `Hi ${name}!` : 'Hi there!';
  return `${who} Welcome to ${restaurant}. I'm your food assistant — what would you like to have today? You can ask me for recommendations too.`;
}
